import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Calendar, 
  Video, 
  FileText, 
  Settings, 
  LogOut,
  User,
  Bell,
  ChevronDown,
  Activity
} from 'lucide-react';
import { authService } from '../../modules/auth/services/authService';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const selectedRole = localStorage.getItem('selectedRole');
  
  // State for ECG dropdown menu
  const [ecgDropdownOpen, setEcgDropdownOpen] = useState(false);

  // Set up automatic token refresh
  useEffect(() => {
    const tokenRefreshInterval = authService.startTokenRefreshInterval();
    
    // Initial token check when component mounts
    authService.checkAndRefreshToken();
    
    // Cleanup interval on unmount
    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, []);

  const handleLogout = () => {
    authService.logoutToRoleSelection();
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ zoom: '90%' }}>
      {/* Header */}
      <div className="bg-white shadow-sm">
        {/* Top Header - Logos and User */}
        <div className="flex justify-between items-center px-6 py-2">
          {/* Left side - MediFusion Logo */}
          <div className="flex items-center">
            <img
              src="/login_medi_logo.png"
              alt="MediFusion"
              className="h-10 w-auto"
            />
          </div>
          
          {/* User Name and GARLI MEDI TECH Logo - Right */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 mr-10">
              <User className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                {selectedRole === 'doctor' && 'Dr. '}{currentUser?.first_name} {currentUser?.last_name}
              </span>
            </div>
            <img
              src="/garlimeditech.png"
              alt="GARLI MEDI TECH"
              className="h-8 w-auto"
            />
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="border-t border-gray-200 px-6 py-2">
          <nav className="flex justify-end">
            <div className="flex items-center space-x-6">
              {/* Home Link */}
              {selectedRole === 'patient' && (
                <Link
                  to="/patient/dashboard"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/patient/dashboard'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Home
                </Link>
              )}
              {selectedRole === 'doctor' && (
                <Link
                  to="/doctor/dashboard"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/doctor/dashboard'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Home
                </Link>
              )}
              {selectedRole === 'clinician' && (
                <Link
                  to="/clinician/dashboard"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/clinician/dashboard'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Home
                </Link>
              )}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/dashboard"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/admin/dashboard'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Home
                </Link>
              )}
              {selectedRole !== 'patient' && selectedRole !== 'doctor' && selectedRole !== 'clinician' && selectedRole !== 'admin' && (
                <Link
                  to="/"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Home
                </Link>
              )}

              {/* Patient Module: Admin Link */}
              {selectedRole === 'patient' && (
                <Link
                  to="/patient/admin"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/admin')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Admin
                </Link>
              )}

              {/* Doctor Module: Calendar Link */}
              {selectedRole === 'doctor' && (
                <Link
                  to="/doctor/calendar"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/calendar')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Calendar
                </Link>
              )}

              {/* Clinician Module: Calendar Link */}
              {selectedRole === 'clinician' && (
                <Link
                  to="/clinician/calendar"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/calendar')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Calendar
                </Link>
              )}

              {/* Admin Module: Bookings Link */}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/booking"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/booking')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Bookings
                </Link>
              )}

              {/* Doctor Module: Patients Link */}
              {selectedRole === 'doctor' && (
                <Link
                  to="/doctor/patientfiles"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/patientfiles')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Patients
                </Link>
              )}

              {/* Clinician Module: Patients Link */}
              {selectedRole === 'clinician' && (
                <Link
                  to="/clinician/patientfiles"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/patientfiles')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Patients
                </Link>
              )}

              {/* Admin Module: Patients Link */}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/patientfiles"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/patientfiles')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Patients
                </Link>
              )}

              {/* Admin Module: Clinics Link */}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/clinics"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/clinics')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Clinics
                </Link>
              )}


              {/* Admin Module: Users Link */}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/users"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/users')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Users
                </Link>
              )}

              {/* Admin Module: Doctors Link */}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/doctors"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/doctors')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Doctors
                </Link>
              )}

              {/* Admin Module: Clinicians Link */}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/clinicians"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname.includes('/clinicians')
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Clinicians
                </Link>
              )}

              {/* Other roles: Calendar and Patients Links */}
              {selectedRole !== 'patient' && selectedRole !== 'doctor' && selectedRole !== 'clinician' && selectedRole !== 'admin' && (
                <>
                  <Link
                    to="/calendar"
                    className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                      location.pathname.includes('/calendar')
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Calendar
                  </Link>
                  <Link
                    to="/patients"
                    className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                      location.pathname.includes('/patients')
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Patients
                  </Link>
                </>
              )}

              {/* News Link */}
              {selectedRole === 'patient' && (
                <Link
                  to="/patient/news"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/patient/news'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  News
                </Link>
              )}
              {selectedRole === 'doctor' && (
                <Link
                  to="/doctor/news"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/doctor/news'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  News
                </Link>
              )}
              {selectedRole === 'clinician' && (
                <Link
                  to="/clinician/news"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/clinician/news'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  News
                </Link>
              )}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/news"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/admin/news'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  News
                </Link>
              )}
              {selectedRole !== 'patient' && selectedRole !== 'doctor' && selectedRole !== 'clinician' && selectedRole !== 'admin' && (
                <Link
                  to="/news"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/news'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  News
                </Link>
              )}

              {/* FAQs Link */}
              {selectedRole === 'patient' && (
                <Link
                  to="/patient/faqs"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/patient/faqs'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  FAQs
                </Link>
              )}
              {selectedRole === 'doctor' && (
                <Link
                  to="/doctor/faqs"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/doctor/faqs'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  FAQs
                </Link>
              )}
              {selectedRole === 'clinician' && (
                <Link
                  to="/clinician/faqs"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/clinician/faqs'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  FAQs
                </Link>
              )}
              {selectedRole === 'admin' && (
                <Link
                  to="/admin/faqs"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/admin/faqs' || location.pathname === '/admin/faq-management'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  FAQs
                </Link>
              )}

              {/* Admin Module: ECG Dropdown */}
              {selectedRole === 'admin' && (
                <div 
                  className="relative"
                  onMouseEnter={() => setEcgDropdownOpen(true)}
                  onMouseLeave={() => setEcgDropdownOpen(false)}
                >
                  <button
                    className={`flex items-center gap-1 text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                      location.pathname.includes('/admin/ecg')
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Activity className="h-4 w-4" />
                    ECG
                    <ChevronDown className={`h-3 w-3 transition-transform ${ecgDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {ecgDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                      <Link
                        to="/admin/ecg-monitor"
                        className={`block px-4 py-2 text-sm transition-colors ${
                          location.pathname === '/admin/ecg-monitor'
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setEcgDropdownOpen(false)}
                      >
                        Monitor
                      </Link>
                      <Link
                        to="/admin/ecg-workstations"
                        className={`block px-4 py-2 text-sm transition-colors ${
                          location.pathname === '/admin/ecg-workstations'
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setEcgDropdownOpen(false)}
                      >
                        Workstations
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {selectedRole !== 'patient' && selectedRole !== 'doctor' && selectedRole !== 'clinician' && selectedRole !== 'admin' && (
                <Link
                  to="/faqs"
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-md ${
                    location.pathname === '/faqs'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  FAQs
                </Link>
              )}

              {/* Log Out Button */}
              <button
                onClick={handleLogout}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Log Out
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Page content - Full width */}
      <main className="py-6">
        <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout; 